import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetIntelligenceReportDetailQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { userLogoSelector } from "store/tempDataSlise"
import { ApiTypes } from "types"

import { ModalLayout } from "layouts/ModalLayout"

import type { KeyIndicatorsTooltipBlock } from "../../convertIntelligenceReportsToTableRows"
import { type PriorityBadgeVariant } from "../PriorityBadge"
import { DrawerHeader } from "./DrawerHeader"
import { KeyIndicatorsSection } from "./KeyIndicatorsSection"
import { RecommendedActionsSection } from "./RecommendedActionsSection"
import { SummarySection } from "./SummarySection"

const DOMAIN_LABELS: Record<string, string> = {
  sizing_runtime: "Sizing & Runtime",
  setpoints: "Setpoints",
  humidity: "Humidity",
  indoor_air_quality: "IAQ",
}

const INDICATOR_DOMAIN_ORDER = ["sizing_runtime", "setpoints", "humidity", "indoor_air_quality"]

const formatMetricName = (metric: string): string =>
  metric
    .replace(/^median_/, "")
    .replace(/_/g, " ")
    .replace(/^\w/, c => c.toUpperCase())

const formatIndicatorText = (ind: ApiTypes.Model.IntelligenceReportIndicator): string => {
  if (ind.bullet_text) return ind.bullet_text
  const meaning = ind.meaning_range ? `${ind.meaning_word} (${ind.meaning_range})` : `(${ind.meaning_word})`
  return `${formatMetricName(ind.metric)}: ${ind.value_raw} → ${meaning}`
}

interface IndicatorBlock {
  title: string
  items: string[]
}

const buildIndicatorBlocks = (indicators: ApiTypes.Model.IntelligenceReportIndicator[]): IndicatorBlock[] => {
  const grouped = new Map<string, string[]>()
  for (const ind of indicators) {
    if (!grouped.has(ind.domain_code)) grouped.set(ind.domain_code, [])
    grouped.get(ind.domain_code)!.push(formatIndicatorText(ind))
  }
  return INDICATOR_DOMAIN_ORDER.filter(domain => grouped.has(domain)).map(domain => ({
    title: DOMAIN_LABELS[domain] || domain,
    items: grouped.get(domain)!,
  }))
}

const SUMMARY_DOMAIN_ORDER = ["overall", "sizing_runtime", "setpoints", "humidity", "indoor_air_quality"]

const StyledDrawerLayout = styled(ModalLayout)`
  > span:first-child {
    display: none;
  }
`

const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  height: calc(100vh - 76px);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 99px;
  }
`

export type IntelligenceReportDrawerRow = ApiTypes.Model.IntelligenceReport & {
  key_indicators_tooltip?: KeyIndicatorsTooltipBlock[]
}

interface Props {
  report: IntelligenceReportDrawerRow | null
  onClose: () => void
}

export const IntelligenceReportDrawer = ({ report, onClose }: Props) => {
  const userLogo = useAppSelector(userLogoSelector)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [fetchDetail, { data: detailResponse, isFetching: isLoadingDetail, isError }] =
    useLazyGetIntelligenceReportDetailQuery()

  useEffect(() => {
    if (report?.report_id) {
      fetchDetail(report.report_id)
        .unwrap()
        .catch(() => {})
    }
  }, [report?.report_id, fetchDetail])

  if (!report) return null

  const detailData = isError ? undefined : detailResponse?.data
  const priorityRaw = String(report.priority ?? "").toLowerCase()
  const priorityVariant: PriorityBadgeVariant =
    priorityRaw === "high" ? "high" : priorityRaw === "medium" ? "medium" : "low"

  const indicatorBlocks = detailData?.key_indicators ? buildIndicatorBlocks(detailData.key_indicators) : []

  const summaryBullets: string[] = []
  if (detailData?.detail_summaries) {
    for (const domain of SUMMARY_DOMAIN_ORDER) {
      const items = detailData.detail_summaries.filter(s => s.domain_code === domain)
      for (const item of items) {
        summaryBullets.push(item.assessment)
      }
    }
  }

  const handleFullReport = async () => {
    if (isGeneratingPdf || !detailData) return
    setIsGeneratingPdf(true)
    try {
      const { generateReportPdf } = await import("../../pdf/generateReportPdf")
      const blob = await generateReportPdf(detailData, userLogo)
      const url = URL.createObjectURL(blob)
      window.open(url, "_blank")
    } catch (err) {
      console.error("PDF generation failed:", err)
      toast.error("PDF download failed. Please try again later.")
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return createPortal(
    <StyledDrawerLayout onClose={onClose} isDrawer isWithoutFooter>
      <DrawerBody>
        <DrawerHeader
          sn={report.sn}
          priority={report.priority}
          priorityVariant={priorityVariant}
          isFullReportDisabled={isGeneratingPdf || isLoadingDetail || !detailData}
          isGeneratingPdf={isGeneratingPdf}
          onFullReport={handleFullReport}
          onClose={onClose}
        />

        <ScrollContent>
          <SummarySection isLoading={isLoadingDetail} bullets={summaryBullets} />
          <KeyIndicatorsSection isLoading={isLoadingDetail} blocks={indicatorBlocks} />
          <RecommendedActionsSection isLoading={isLoadingDetail} actions={detailData?.actions} />
        </ScrollContent>
      </DrawerBody>
    </StyledDrawerLayout>,
    document.body,
  )
}
